import { useCallback, useReducer, useState } from "react";
import { singletonHook } from "react-singleton-hook";
import { getId } from "@fluentui/utilities";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { IAppContext, IClientComponent } from "@cayo/shared/components/App/appContext";
import { IShowControlHandler } from "@cayo/shared/components/App/types";
import { appSignals, showActionParams } from "@cayo/shared/components/App/utils";
import { showError } from "@cayo/shared/components/App/utils/showError";
import panelReducer from "@cayo/shared/components/AppRoots/PropertiesPanelHolder/propertiesPanelReducer";
import { IPropertiesPanel } from "@cayo/shared/components/AppRoots/PropertiesPanelHolder/types";
import ajax from "@cayo/shared/libs/ajax";
import logger from "@cayo/shared/libs/logger";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { urlUtils } from "@cayo/shared/utils/url";

export const appReducer = (old: IAppContext, newModel: Partial<IAppContext>) => {
  return { ...old, ...newModel } as IAppContext;
};

const initialPanels: IPropertiesPanel[] = [];

type ComponentModule = { default: any };
type ComponentLoader = () => Promise<ComponentModule>;

const sharedComponentsRoot = "..";
const coreComponentsRoot = "../../../../core/src/components";

const sharedComponentLoaders = (
  import.meta as ImportMeta & {
    glob: (pattern: string | string[]) => Record<string, ComponentLoader>;
  }
).glob([
  "../**/*.{ts,tsx,js,jsx}",
  "!../**/*.test.{ts,tsx,js,jsx}",
  "!../**/*.spec.{ts,tsx,js,jsx}",
  "!../**/*.stories.{ts,tsx,js,jsx}",
  "!../**/__tests__/**",
]);

const coreComponentLoaders = (
  import.meta as ImportMeta & {
    glob: (pattern: string | string[]) => Record<string, ComponentLoader>;
  }
).glob([
  "../../../../core/src/components/**/*.{ts,tsx,js,jsx}",
  "!../../../../core/src/components/**/*.test.{ts,tsx,js,jsx}",
  "!../../../../core/src/components/**/*.spec.{ts,tsx,js,jsx}",
  "!../../../../core/src/components/**/*.stories.{ts,tsx,js,jsx}",
  "!../../../../core/src/components/**/__tests__/**",
]);

const componentRoots = [sharedComponentsRoot, coreComponentsRoot] as const;
const componentLoaders = { ...sharedComponentLoaders, ...coreComponentLoaders };
const componentLoadersLower = new Map(
  Object.entries(componentLoaders).map(([key, loader]) => [key.toLowerCase(), loader])
);

const resolveComponentLoader = (folder: string, id: string) => {
  const safeFolder = folder ? folder.replace(/^\/+/, "").replace(/\/?$/, "/") : "";
  const safeId = id.replace(/^\/+/, "");

  for (const root of componentRoots) {
    const basePath = `${root}/${safeFolder}${safeId}`;

    const candidates = [
      `${basePath}.tsx`,
      `${basePath}.ts`,
      `${basePath}.jsx`,
      `${basePath}.js`,
      `${basePath}/index.tsx`,
      `${basePath}/index.ts`,
      `${basePath}/index.jsx`,
      `${basePath}/index.js`,
    ];

    for (const candidate of candidates) {
      const loader =
        componentLoaders[candidate] ?? componentLoadersLower.get(candidate.toLowerCase());
      if (loader) {
        return loader;
      }
    }
  }

  return undefined;
};

const _useDetachedSchemes = () => {
  const [propertiesPanels, setPropertiesPanels] = useReducer(panelReducer, []);

  const [clientComponents, setClientComponents] = useState<IClientComponent[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const hideClientComponents = useCallback(() => setClientComponents([]), []);

  const showControl = useCallback(
    (prms: IShowControlHandler) => {
      const id = prms.controlId!;
      const parameters = prms?.parameters;

      if (prms.folder && prms.folder.indexOf(".") > 0) {
        appSignals.send("showClientWizard", {
          schemeId: prms.controlId!,
          package: prms.folder,
          reloadAfterClose: prms.reloadAfterClose,
          parameters,
        });
        return;
      }

      if (id === "scheme") {
        if (prms.parameters) {
          showPropertiesPanel(
            typeof prms?.parameters === "string"
              ? { id: prms.parameters, parentId: (prms as any).parentContextId }
              : prms.parameters
          );
        } else {
          closePropertiesPanel();
        }

        return;
      }

      // TODO: delete this bullshit
      const reloadAfterClose =
        prms.reloadAfterClose === false
          ? false
          : prms.reloadAfterClose ||
            id.toLowerCase().indexOf("license") >= 0 ||
            id.toLowerCase().indexOf("activation") >= 0;

      const folder = prms.folder ? `${prms.folder}/` : "";
      const componentLoader = resolveComponentLoader(folder, id);

      if (!componentLoader) {
        appSignals.send("showClientWizard", {
          schemeId: prms.controlId!,
          package: prms.folder,
          reloadAfterClose: prms.reloadAfterClose,
        });
        return;
      }

      componentLoader()
        .then((module) => {
          try {
            const resolvedModule = module as ComponentModule;
            const clientParameters: any =
              Object.prototype.toString.call(prms?.parameters) === "[object Array]"
                ? { items: prms.parameters }
                : prms.parameters;

            setClientComponents([
              ...(clientComponents || []),
              {
                component: resolvedModule.default,
                parameters: {
                  id,
                  reloadAfterClose: reloadAfterClose,
                  onRefresh: (prms as any)?.onRefresh,
                  ...clientParameters,
                  onClose: function onCloseHandler(result: boolean) {
                    hideClientComponents();

                    prms.parameters?.onClose && prms.parameters.onClose(result);
                    prms.refreshAfterClose && refreshPropertiesPanel();

                    const isDeleteAction = (prms?.parameters?.url as string)?.endsWith("/delete");
                    const closeHandler = prms.parameters?.closeHandler;
                    if (
                      (closeHandler && prms.controlId !== "LegacyCollectActionParametersDialog") ||
                      isDeleteAction
                    ) {
                      closePropertiesPanel();
                    }
                  },
                },
              },
            ]);
          } catch (e) {
            logger.log(e);
          }
        })
        .catch((e: any) => {
          if (e.code === "MODULE_NOT_FOUND") {
            appSignals.send("showClientWizard", {
              schemeId: prms.controlId!,
              package: prms.folder,
              reloadAfterClose: prms.reloadAfterClose,
            });
          } else {
            showError(e);
            logger.error(e);
          }

          return;
        });
    },
    [clientComponents, propertiesPanels]
  );

  const loadObject = async (objectPath: string, noRetry?: boolean | undefined): Promise<any> => {
    setIsLoading(true);

    try {
      const object = await ajax.getClient("v0/" + objectPath, "GET", undefined, undefined, {
        $select: "*",
      });

      return object;
    } catch (e: any) {
      if (noRetry) {
        return undefined;
      }

      if (e.status === HttpStatus.NOT_FOUND && urlUtils.isLastSegmentTypeCast(objectPath)) {
        const fallbackPath = urlUtils.replaceLastSegment(objectPath, "");
        const fallbackObject = await loadObject(fallbackPath, true);
        if (fallbackObject) {
          return fallbackObject;
        }
      }

      logger.debug(e);
      await ajaxUtils.handleError(e);
    } finally {
      setIsLoading(false);
    }

    return undefined;
  };

  const showPropertiesPanel = useCallback(
    (scheme: IPropertiesPanel) => {
      scheme.key = new Date().toISOString();
      if (scheme.id) {
        const existingPanel = propertiesPanels.find(
          (p) =>
            p.id === scheme.id &&
            p.parentId === scheme.parentId &&
            (scheme.property ? p.property : true)
        );

        if (existingPanel) {
          setPropertiesPanels({ type: "show", panel: existingPanel });
          return;
        }

        if (scheme.isNewObject || scheme.id.endsWith("/new")) {
          if (scheme.isNewObject) {
            scheme.parentId = getId("new_panel");
          }
          setPropertiesPanels({ type: "show", panel: scheme });
        } else {
          const isComplex = scheme.id.indexOf("returnParameters=") > 0 || !!scheme.property?.name;
          setIsLoading(true);

          if (scheme.isPicker) {
            if (!scheme.parentId && propertiesPanels.length) {
              scheme.parentId = [...propertiesPanels].reverse().find((p) => !!p.id)?.id;
            }
            setPropertiesPanels({ type: "show", panel: scheme });
            setIsLoading(false);
          } else {
            (isComplex ? Promise.resolve() : loadObject(scheme.id))
              .then((object) => {
                if (object || isComplex) {
                  const objects = (scheme as any).objects as Entity[];
                  if (!object && objects?.length) {
                    object = objects[0];
                  }
                  scheme.object = object;
                  scheme.id = object.objectPath || scheme?.property?.parentObjectPath;
                  if (isComplex && scheme?.property) {
                    scheme.isNewObject = true;
                  }
                  setPropertiesPanels({ type: "show", panel: scheme });
                }
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        }
      }
    },
    [propertiesPanels]
  );

  const updatePropertiesPanel = useCallback(
    (scheme: IPropertiesPanel) => {
      if (!scheme?.id) {
        return;
      }

      const existingPanel = propertiesPanels.find((p) => p.parentId === scheme?.parentId);
      if (existingPanel) {
        loadObject(scheme.id).then((object) => {
          if (object) {
            scheme.id = object.objectPath;
            scheme.object = object;
            setPropertiesPanels({ type: "update", panel: scheme });
          }
        });
      }
    },
    [propertiesPanels]
  );

  const refreshPropertiesPanel = useCallback(() => {
    const existingPanel =
      propertiesPanels.length > 0 ? propertiesPanels[propertiesPanels.length - 1] : undefined;
    if (existingPanel && existingPanel.id) {
      return loadObject(existingPanel.id!).then((object) => {
        if (object) {
          existingPanel.object = object;
          existingPanel.id = object.objectPath;
          setPropertiesPanels({ type: "update", panel: existingPanel });
        }
      });
    }

    return Promise.resolve();
  }, [propertiesPanels]);

  const removeClientComponent = useCallback(() => {
    if (clientComponents?.length && clientComponents.length > 0) {
      clientComponents.splice(clientComponents.length - 1);
      setClientComponents([...clientComponents]);
    }
  }, [clientComponents]);

  const closePropertiesPanel = useCallback(
    (parentId?: string) => {
      setPropertiesPanels({ type: "close", panel: { parentId } });

      // if (propertiesPanels?.length && propertiesPanels.length > 0) {
      //   propertiesPanels.splice(propertiesPanels.length - 1);

      // }
    },
    [propertiesPanels]
  );

  const showAction = useCallback((prms: showActionParams & { gridId?: string | undefined }) => {
    appSignals.send("showAction", {
      schemeId: prms.schemeId,
      objects: prms.objects,
      independent: prms?.gridId === "dummy",
      onSaveComplete: prms.onSaveComplete,
      actionParameters: prms.actionParameters,
      template: prms.template,
    });

    return Promise.resolve();
  }, []);

  return {
    propertiesPanels,
    clientComponents,
    showPropertiesPanel,
    updatePropertiesPanel,
    refreshPropertiesPanel,
    closePropertiesPanel,
    closeAllPropertiesPanels: () => setPropertiesPanels({ type: "closeAll" }),
    removeClientComponent,
    showControl,
    showAction,
    hideClientComponents,
    isLoading,
  };
};

export const useSchemes = singletonHook(
  {
    isLoading: false,
    clientComponents: [],
    propertiesPanels: initialPanels,
    showPropertiesPanel: () => null,
    updatePropertiesPanel: () => null,
    refreshPropertiesPanel: () => Promise.resolve(),
    closePropertiesPanel: () => null,
    closeAllPropertiesPanels: () => null,
    showControl: () => null,
    showAction: () => Promise.resolve(),
    removeClientComponent: () => null,
    hideClientComponents: () => null,
  },
  _useDetachedSchemes
);
